import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import { AREAS, TOPICS, LEVELS, KINDS, Level, Topic, Formula as FormulaT, topicById } from "../COMPONENTS/mekportal/curriculum";
import { useProgress, conceptKey } from "../COMPONENTS/mekportal/progress";
import "../CSS/mekportal.scss";

const topicUrl = (id: string) => `${routeUrls.MEKPORTAL}/${id}`;
// Two pages that sit beside the topics
const SHEET = "formler";
const DRILL = "forhor";
const NUM_FORMULAS = TOPICS.reduce((n, t) => n + t.formulas.length, 0);
const NUM_QUESTIONS = TOPICS.reduce((n, t) => n + t.quiz.length, 0);
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

const Bar = ({ value, label }: { value: number; label: string }) => (
  <span className="mek__bar" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
    <span style={{ width: `${value}%` }} />
  </span>
);

// Formulas are plain strings: "x_n" and "x_{n+1}" become subscripts, "a^p",
// "a^(1/2)" and "a^{…}" superscripts
const WORD = /[A-Za-z0-9α-ωÅÄÖåäö]/;
const Formula = ({ s }: { s: string }) => {
  const out: (string | JSX.Element)[] = [];
  let buf = "";
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if ((c === "_" || c === "^") && i + 1 < s.length) {
      let j = i + 1;
      let arg: string;
      const open = s[j];
      if (open === "{" || open === "(") {
        const close = open === "{" ? "}" : ")";
        let depth = 0;
        for (; j < s.length; j++) {
          if (s[j] === open) depth++;
          else if (s[j] === close && --depth === 0) break;
        }
        arg = s.slice(i + 2, j);
        j++;
      } else {
        while (j < s.length && WORD.test(s[j])) j++;
        arg = s.slice(i + 1, j);
      }
      if (buf) out.push(buf);
      buf = "";
      out.push(c === "_" ? <sub key={i}>{arg}</sub> : <sup key={i}>{arg}</sup>);
      i = j;
    } else {
      buf += c;
      i++;
    }
  }
  if (buf) out.push(buf);
  return <>{out}</>;
};

const FormulaList = ({ items }: { items: FormulaT[] }) => (
  <dl className="mek__formulas">
    {items.map((f) => (
      <div key={f.name}>
        <dt>{f.name}</dt>
        <dd>
          <code><Formula s={f.expr} /></code>
          {f.note && <small><Formula s={f.note} /></small>}
        </dd>
      </div>
    ))}
  </dl>
);

const LevelTag = ({ level }: { level: Level }) => <span className={`mek__level mek__level--${level}`}>{LEVELS[level]}</span>;

export const MekPortalView = ({ topicId }: { topicId?: string }) => {
  usePageBackground();
  const { done, toggle } = useProgress();
  const topic = topicId ? topicById(topicId) : undefined;
  const count = (t: Topic) => t.concepts.filter((c) => done.has(conceptKey(t.id, c))).length;

  useEffect(() => window.scrollTo(0, 0), [topicId]);

  return (
    <div className="mek">
      {topicId === SHEET ? (
        <Sheet />
      ) : topicId === DRILL ? (
        <Drill started={TOPICS.filter((t) => count(t) > 0)} />
      ) : topic ? (
        <TopicPage topic={topic} done={done} toggle={toggle} />
      ) : (
        <Hub count={count} />
      )}
    </div>
  );
};

const Hub = ({ count }: { count: (t: Topic) => number }) => {
  const [level, setLevel] = useState<Level | "alla">("alla");
  const [q, setQ] = useState("");

  const total = TOPICS.reduce((n, t) => n + t.concepts.length, 0);
  const ticked = TOPICS.reduce((n, t) => n + count(t), 0);
  const next = TOPICS.find((t) => count(t) < t.concepts.length);

  const match = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (t: Topic) =>
      (level === "alla" || t.level === level) &&
      (!s ||
        [t.title, t.summary, ...t.concepts, ...t.formulas.map((f) => f.name)].some((x) => x.toLowerCase().includes(s)));
  }, [q, level]);

  const areas = AREAS.map((a) => ({ ...a, topics: a.topics.filter(match) })).filter((a) => a.topics.length);

  return (
    <div className="mek__inner">
      <NavLink to={routeUrls.PROJEKT} className="mek__back">← Projekt</NavLink>
      <header className="mek__header">
        <div>
          <h1 className="mek__title">Maskin&shy;ingenjör<span>.</span></h1>
          <p className="mek__sub">
            Det en maskiningenjör behöver kunna, i en ordning som bygger på sig själv. Bocka av begreppen när du kan dem.
          </p>
        </div>
        <div className="mek__status">
          <div className="mek__total">
            <span>
              <b>{ticked}</b> av {total} begrepp · {pct(ticked, total)} %
            </span>
            <Bar value={pct(ticked, total)} label="Total framsteg" />
          </div>
          {next && (
            <NavLink to={topicUrl(next.id)} className="mek__next">
              {ticked ? "Fortsätt med" : "Börja med"} <b>{next.title}</b> →
            </NavLink>
          )}
          <dl className="mek__legend">
            <div><dt><LevelTag level="grund" /></dt><dd>Det du behöver först</dd></div>
            <div><dt><LevelTag level="karna" /></dt><dd>Vardagen som konstruktör</dd></div>
            <div><dt><LevelTag level="fordjup" /></dt><dd>När grunden sitter</dd></div>
          </dl>
        </div>
      </header>

      <div className="mek__toolcards">
        <NavLink to={topicUrl(DRILL)} className="mek__toolcard">
          <b>Förhör mig</b>
          <span>{NUM_QUESTIONS} frågor från alla ämnen, i slumpad ordning</span>
        </NavLink>
        <NavLink to={topicUrl(SHEET)} className="mek__toolcard">
          <b>Formelsamling</b>
          <span>Alla {NUM_FORMULAS} formler på en sida, sökbar och utskrivbar</span>
        </NavLink>
      </div>

      <div className="mek__tools">
        <div className="mek__chips" role="group" aria-label="Nivå">
          {(["alla", "grund", "karna", "fordjup"] as const).map((l) => (
            <button key={l} type="button" className={level === l ? "on" : undefined} aria-pressed={level === l} onClick={() => setLevel(l)}>
              {l === "alla" ? "Alla" : LEVELS[l]} <span>{l === "alla" ? TOPICS.length : TOPICS.filter((t) => t.level === l).length}</span>
            </button>
          ))}
        </div>
        <label className="mek__search">
          <span className="mek__sr">Sök</span>
          <input type="search" placeholder="Sök ämne, begrepp eller formel" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>

      {areas.length === 0 && <p className="mek__empty">Inget matchar "{q}".</p>}
      {areas.map((a) => (
        <section key={a.id} className="mek__area">
          <h2 className="mek__area-title">
            {a.title}
            <span>
              {a.topics.reduce((n, t) => n + count(t), 0)}/{a.topics.reduce((n, t) => n + t.concepts.length, 0)}
            </span>
          </h2>
          <div className="mek__grid">
            {a.topics.map((t) => {
              const n = count(t);
              return (
                <NavLink key={t.id} to={topicUrl(t.id)} className={`mek__card${n === t.concepts.length ? " mek__card--done" : ""}`}>
                  <span className="mek__card-top">
                    <LevelTag level={t.level} />
                    <span className="mek__card-count">
                      {n === t.concepts.length ? "Klart ✓" : `${n}/${t.concepts.length}`}
                    </span>
                  </span>
                  <span className="mek__card-title">{t.title}</span>
                  <span className="mek__card-sum">{t.summary}</span>
                  <Bar value={pct(n, t.concepts.length)} label={`Framsteg i ${t.title}`} />
                </NavLink>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

const TopicPage = ({ topic: t, done, toggle }: { topic: Topic; done: Set<string>; toggle: (k: string) => void }) => {
  const i = TOPICS.indexOf(t);
  const prev = TOPICS[i - 1];
  const next = TOPICS[i + 1];
  const area = AREAS.find((a) => a.topics.includes(t))!;
  const n = t.concepts.filter((c) => done.has(conceptKey(t.id, c))).length;

  return (
    <article className="mek__inner mek__topic">
      <NavLink to={routeUrls.MEKPORTAL} className="mek__back">← Alla ämnen</NavLink>
      <p className="mek__eyebrow">
        {area.title} · <LevelTag level={t.level} />
      </p>
      <h1 className="mek__title mek__title--topic">{t.title}<span>.</span></h1>
      <p className="mek__why">{t.why}</p>

      <section className="mek__sec">
        <h2>
          Begrepp <span className="mek__muted">{n}/{t.concepts.length}</span>
        </h2>
        <Bar value={pct(n, t.concepts.length)} label="Framsteg i ämnet" />
        {n === t.concepts.length && (
          <p className="mek__done" role="status">
            Snyggt, du har bockat av allt i {t.title.toLowerCase()}.
            {next && <> <NavLink to={topicUrl(next.id)}>Vidare till {next.title} →</NavLink></>}
          </p>
        )}
        <ul className="mek__checks">
          {t.concepts.map((c) => {
            const k = conceptKey(t.id, c);
            return (
              <li key={c}>
                <label className={done.has(k) ? "on" : undefined}>
                  <input type="checkbox" checked={done.has(k)} onChange={() => toggle(k)} />
                  <span>{c}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </section>

      {t.formulas.length > 0 && (
        <section className="mek__sec">
          <h2>Formler</h2>
          <FormulaList items={t.formulas} />
        </section>
      )}

      <section className="mek__sec">
        <h2>Vanliga fällor</h2>
        <ul className="mek__pitfalls">
          {t.pitfalls.map((p) => <li key={p}>{p}</li>)}
        </ul>
      </section>

      <section className="mek__sec">
        <h2>Testa dig själv</h2>
        {t.quiz.map((x) => (
          <details key={x.q} className="mek__quiz">
            <summary>{x.q}</summary>
            <p>{x.a}</p>
          </details>
        ))}
      </section>

      <section className="mek__sec">
        <h2>Lär dig mer</h2>
        <ul className="mek__res">
          {t.resources.map((r) => (
            <li key={r.title}>
              <span className={`mek__kind mek__kind--${r.kind}`}>{KINDS[r.kind]}</span>
              <span>
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title} ↗</a>
                ) : (
                  <b>{r.title}</b>
                )}
                <small>{r.note}</small>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <nav className="mek__pager" aria-label="Fler ämnen">
        {prev ? <NavLink to={topicUrl(prev.id)}>← {prev.title}</NavLink> : <span />}
        {next && <NavLink to={topicUrl(next.id)} className="mek__pager-next">{next.title} →</NavLink>}
      </nav>
    </article>
  );
};

const Sheet = () => {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const rows = TOPICS.map((t) => ({
    t,
    items: t.formulas.filter((f) => !s || `${f.name} ${f.expr} ${t.title}`.toLowerCase().includes(s)),
  })).filter((r) => r.items.length);

  return (
    <div className="mek__inner mek__topic">
      <NavLink to={routeUrls.MEKPORTAL} className="mek__back">← Alla ämnen</NavLink>
      <h1 className="mek__title mek__title--topic">Formelsamling<span>.</span></h1>
      <p className="mek__why">Alla formler från portalen, ämne för ämne.</p>
      <div className="mek__tools">
        <label className="mek__search">
          <span className="mek__sr">Sök formel</span>
          <input type="search" placeholder="Sök, t.ex. böj, Reynolds eller lager" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <button type="button" className="mek__btn" onClick={() => window.print()}>Skriv ut</button>
      </div>
      {rows.length === 0 && <p className="mek__empty">Ingen formel matchar "{q}".</p>}
      {rows.map(({ t, items }) => (
        <section key={t.id} className="mek__sec mek__sheet-sec">
          <h2><NavLink to={topicUrl(t.id)}>{t.title}</NavLink></h2>
          <FormulaList items={items} />
        </section>
      ))}
    </div>
  );
};

interface Card { topic: Topic; q: string; a: string }
const shuffle = <T,>(xs: T[]): T[] => {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
const deckFor = (topics: Topic[]): Card[] => shuffle(topics.flatMap((t) => t.quiz.map((x) => ({ topic: t, ...x }))));

// One question at a time; ones you didn't know go back into the pile
const Drill = ({ started }: { started: Topic[] }) => {
  const [scope, setScope] = useState<"alla" | "paborjade">("alla");
  const [deck, setDeck] = useState<Card[]>(() => deckFor(TOPICS));
  const [shown, setShown] = useState(false);
  const [right, setRight] = useState(0);
  const [tries, setTries] = useState(0);

  const restart = (sc: typeof scope) => {
    setScope(sc);
    setDeck(deckFor(sc === "alla" ? TOPICS : started));
    setShown(false);
    setRight(0);
    setTries(0);
  };

  const answer = (knew: boolean) => {
    setTries((n) => n + 1);
    if (knew) setRight((n) => n + 1);
    setDeck(([c, ...rest]) => (knew ? rest : [...rest, c]));
    setShown(false);
  };

  const card = deck[0];
  return (
    <div className="mek__inner mek__topic">
      <NavLink to={routeUrls.MEKPORTAL} className="mek__back">← Alla ämnen</NavLink>
      <h1 className="mek__title mek__title--topic">Förhör<span>.</span></h1>
      <p className="mek__why">Svara i huvudet, visa svaret och var ärlig. Det du inte kunde kommer tillbaka senare.</p>
      <div className="mek__tools">
        <div className="mek__chips" role="group" aria-label="Vilka ämnen">
          <button type="button" className={scope === "alla" ? "on" : undefined} aria-pressed={scope === "alla"} onClick={() => restart("alla")}>
            Alla ämnen
          </button>
          <button
            type="button"
            className={scope === "paborjade" ? "on" : undefined}
            aria-pressed={scope === "paborjade"}
            disabled={!started.length}
            title={started.length ? undefined : "Bocka av något begrepp först"}
            onClick={() => restart("paborjade")}
          >
            Ämnen jag påbörjat <span>{started.length}</span>
          </button>
        </div>
        <span className="mek__score">
          {right} rätt av {tries} · {deck.length} kvar
        </span>
      </div>

      {card ? (
        <div className="mek__drill" aria-live="polite">
          <NavLink to={topicUrl(card.topic.id)} className="mek__drill-topic">{card.topic.title}</NavLink>
          <p className="mek__drill-q">{card.q}</p>
          {shown ? (
            <>
              <p className="mek__drill-a">{card.a}</p>
              <div className="mek__drill-btns">
                <button type="button" className="mek__btn mek__btn--ok" onClick={() => answer(true)}>Jag kunde det</button>
                <button type="button" className="mek__btn" onClick={() => answer(false)}>Inte riktigt</button>
              </div>
            </>
          ) : (
            <button type="button" className="mek__btn mek__btn--main" onClick={() => setShown(true)}>Visa svar</button>
          )}
        </div>
      ) : (
        <div className="mek__drill">
          <p className="mek__drill-q">Klart! Du kunde {right} av {tries} försök.</p>
          <button type="button" className="mek__btn mek__btn--main" onClick={() => restart(scope)}>Kör igen</button>
        </div>
      )}
    </div>
  );
};
